import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { toast } from "sonner";

export default function BookingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get(`/bookings/${id}`)
      .then(({ data }) => setBooking(data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "Not found"));
  }, [id]);

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/checkout/session", {
        booking_id: id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <div className="bg-brand-slate min-h-screen">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 py-10">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-brand-red font-bold" data-testid="booking-error">
            {error}
          </div>
        )}
        {booking && (
          <div className="bg-white rounded-[32px] shadow-sm border border-slate-100 overflow-hidden" data-testid={`booking-detail-${booking.id}`}>
            <div className="bg-brand-blue text-white px-6 py-6">
              <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-gold">
                Order #{booking.id.slice(0, 8).toUpperCase()}
              </p>
              <h1 className="text-3xl font-black uppercase tracking-tightest mt-1">
                {booking.status === "pending_payment" ? "Complete Payment" : "Booking"}
              </h1>
            </div>
            <div className="px-6 py-5">
              <ul className="divide-y divide-slate-100">
                {booking.items.map((it) => (
                  <li key={it.item_id} className="py-3 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-brand-blue text-brand-gold flex items-center justify-center">
                      <i className={`fas ${it.icon}`} />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-black uppercase text-brand-blue">{it.name}</p>
                      <p className="text-[10px] font-bold text-slate-500">
                        {it.quantity} × ${it.price_per_day.toFixed(2)}/day × {booking.rental_days}d
                      </p>
                    </div>
                    <p className="font-black text-sm text-brand-red">${it.line_total.toFixed(2)}</p>
                  </li>
                ))}
              </ul>
              <div className="mt-4 pt-4 border-t-2 border-dashed border-slate-200 space-y-1.5 text-sm">
                <Row label="Subtotal" value={`$${booking.subtotal.toFixed(2)}`} />
                <Row label="Tax" value={`$${booking.tax.toFixed(2)}`} />
                <Row label="Total" value={`$${booking.total.toFixed(2)}`} bold />
              </div>
              {booking.status === "pending_payment" ? (
                <button
                  onClick={pay}
                  disabled={busy}
                  className="btn-primary mt-6 w-full py-4 rounded-2xl font-black uppercase tracking-widest text-sm disabled:opacity-50"
                  data-testid="booking-pay-button"
                >
                  {busy ? <><i className="fas fa-spinner fa-spin mr-2" /> Redirecting</> : <><i className="fas fa-lock mr-2" /> Pay ${booking.total.toFixed(2)}</>}
                </button>
              ) : (
                <Link
                  to="/dashboard"
                  className="btn-primary mt-6 w-full py-4 rounded-2xl font-black uppercase tracking-widest text-sm flex items-center justify-center"
                  data-testid="booking-dashboard-link"
                >
                  View Dashboard
                </Link>
              )}

              {/* Access codes + lock-close confirmation */}
              {(booking.pickup_code || booking.dropoff_code) && (
                <AccessCodesPanel booking={booking} onUpdated={setBooking} />
              )}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className={`flex justify-between ${bold ? "font-black uppercase text-brand-blue text-base pt-2" : "font-bold text-slate-600"}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

function AccessCodesPanel({ booking, onUpdated }) {
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);

  const fmt = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
    catch { return iso; }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a photo first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/bookings/${booking.id}/lock-confirmation`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo uploaded — thanks! Admin will finalize.");
      const { data } = await api.get(`/bookings/${booking.id}`);
      onUpdated(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <div className="mt-8 space-y-4" data-testid="access-codes-panel">
      <div className="border-t-2 border-dashed border-slate-200 pt-6">
        <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-red mb-3">Your Pod Access</p>
      </div>

      {booking.pickup_code && (
        <div className="bg-brand-blue text-white rounded-2xl p-5" data-testid="pickup-code-block">
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-gold mb-1">Pickup Code</p>
          <p className="font-mono font-black text-4xl tracking-widest mb-2">{booking.pickup_code}</p>
          <p className="text-xs font-bold text-white/80">Use between {fmt(booking.pickup_window_start)} → {fmt(booking.pickup_window_end)}</p>
        </div>
      )}

      {booking.dropoff_code && (
        <div className="bg-brand-red text-white rounded-2xl p-5" data-testid="dropoff-code-block">
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-white mb-1">Dropoff Code</p>
          <p className="font-mono font-black text-4xl tracking-widest mb-2">{booking.dropoff_code}</p>
          <p className="text-xs font-bold text-white/80">Return between {fmt(booking.dropoff_window_start)} → {fmt(booking.dropoff_window_end)}</p>
        </div>
      )}

      {booking.access_notes && (
        <div className="bg-amber-50 border-l-4 border-brand-gold rounded-r-xl p-4">
          <p className="text-[10px] font-black uppercase tracking-widest text-amber-800 mb-1">Note from your operator</p>
          <p className="text-sm font-bold text-amber-900">{booking.access_notes}</p>
        </div>
      )}

      {/* Lock-close photo uploader */}
      {booking.lock_confirmation_at ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 text-center" data-testid="lock-photo-confirmed">
          <i className="fas fa-check-circle text-3xl text-emerald-500 mb-2" />
          <p className="text-sm font-black text-emerald-800 uppercase tracking-widest">Lock Confirmed</p>
          <p className="text-xs text-emerald-700 font-bold mt-1">Uploaded {fmt(booking.lock_confirmation_at)}</p>
        </div>
      ) : (
        <form onSubmit={submit} className="bg-slate-50 rounded-2xl p-5 space-y-3" data-testid="lock-photo-form">
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-brand-blue mb-1">Finish Your Rental</p>
            <p className="text-sm text-slate-600 font-bold">After you drop off all gear and lock the pod, upload a photo of the closed lock. This triggers your deposit release.</p>
          </div>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="w-full text-sm font-bold text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-[10px] file:font-black file:uppercase file:tracking-widest file:bg-brand-blue file:text-white"
            data-testid="lock-photo-input"
          />
          <button type="submit" disabled={uploading || !file} className="btn-primary w-full py-3 rounded-xl font-black uppercase text-[10px] tracking-widest disabled:opacity-50" data-testid="lock-photo-submit">
            {uploading ? <><i className="fas fa-spinner fa-spin mr-2" /> Uploading</> : <><i className="fas fa-camera mr-2" /> Confirm Pod Locked</>}
          </button>
        </form>
      )}
    </div>
  );
}
